import { useState, useEffect, useCallback, useRef } from 'react';
import { ladeEinstellungen, aendereEinstellungen, einstellungenStand, useBenutzerEinstellungen } from '../../services/benutzerEinstellungen';
import { layoutAusEinstellungen, layoutAusCookie, cookieAusLayout, dashboardAenderung } from './dashboardEinstellungen';
import { schalteBereich } from './bereiche';

// #471 (Scheibe 1 aus #313): Spalten ein-/ausblenden, Heute-Kalender und die
// Breitenmessung der Pool-Spalte. Code unveraendert aus Dashboard.js
// uebernommen (Muster aus #312: Zustand im Hook, das Dashboard ruft ihn auf).
// Die Vorlieben liegen je Benutzer in einem Cookie; den Benutzer kennt das
// Dashboard, deshalb kommen currentUser und getCurrentUserEmail herein.
// `loading` braucht die Breitenmessung: Sie misst neu, sobald die Spalten nach
// dem Laden im DOM stehen.
// #564: Maßgeblich ist seitdem der Schlüssel `dashboard` der Benutzer-
// Einstellungen auf dem Server (gleich auf allen Geräten). Das Cookie bleibt
// als Spiegel, damit das erste Bild ohne Warten auf den Server stimmt, und
// wird einmal übernommen, solange der Server noch nichts hat.
// #569: Statt zweier Schalter (Mails, Teams) eine Liste ausgeblendeter
// Bereiche und die Reihenfolge (dashboard/bereiche.js). hideEmails und
// hideTeams bleiben als abgeleitete Werte für die Augen-Knöpfe.
// #650: dazu `eingeblendet` für Bereiche, die von sich aus aus sind.

const mitBereich = (liste, id, aus) => (aus ? [...liste.filter((x) => x !== id), id] : liste.filter((x) => x !== id));

export default function useDashboardLayout({ currentUser, getCurrentUserEmail, loading }) {
  const [ausgeblendet, setAusgeblendet] = useState([]);
  const [reihenfolge, setReihenfolge] = useState([]);
  const [eingeblendet, setEingeblendet] = useState([]);
  const hideEmails = ausgeblendet.includes('mails');
  const hideTeams = ausgeblendet.includes('teams');
  const setHideEmails = useCallback((aus) => setAusgeblendet((l) => mitBereich(l, 'mails', !!aus)), []);
  const setHideTeams = useCallback((aus) => setAusgeblendet((l) => mitBereich(l, 'teams', !!aus)), []);
  // Toggle: show embedded DayCalendar instead of compact today's events list
  const [showTodayCalendar, setShowTodayCalendar] = useState(false);
  // "Heute"-Kalender: Höhe dynamisch bis zum unteren Viewport-Rand, damit nichts unten rausläuft
  const todayCalendarWrapRef = useRef(null);
  const [todayCalendarHeight, setTodayCalendarHeight] = useState(null);

  // Responsive: observe width of Tickets column
  const ticketsSectionRef = useRef(null);
  const ticketsHeaderRef = useRef(null);
  const [isTicketsNarrow, setIsTicketsNarrow] = useState(false);
  useEffect(() => {
    const measure = () => {
      const sectionEl = ticketsSectionRef.current;
      const headerEl = ticketsHeaderRef.current;
      if (!sectionEl && !headerEl) return;
      const sectionWidth = sectionEl ? sectionEl.getBoundingClientRect().width : Infinity;
      const headerWidth = headerEl ? headerEl.getBoundingClientRect().width : Infinity;
      const width = Math.min(sectionWidth, headerWidth);
      setIsTicketsNarrow(width < 430);
    };

    const observers = [];
    if (typeof ResizeObserver !== 'undefined') {
      if (ticketsSectionRef.current) {
        const obs1 = new ResizeObserver(measure);
        obs1.observe(ticketsSectionRef.current);
        observers.push(obs1);
      }
      if (ticketsHeaderRef.current) {
        const obs2 = new ResizeObserver(measure);
        obs2.observe(ticketsHeaderRef.current);
        observers.push(obs2);
      }
    }
    window.addEventListener('resize', measure);
    // Initial measurement next tick
    requestAnimationFrame(measure);
    return () => {
      observers.forEach(o => o.disconnect());
      window.removeEventListener('resize', measure);
    };
  }, [loading]);

  // Dynamische Höhe für den eingebetteten "Heute"-Kalender (endet am unteren Bildschirmrand)
  useEffect(() => {
    if (!showTodayCalendar) return;
    let rafId = null;
    const measure = () => {
      const el = todayCalendarWrapRef.current;
      if (!el) return;
      const vvH = (typeof window !== 'undefined' && window.visualViewport && window.visualViewport.height) ? window.visualViewport.height : window.innerHeight;
      const rect = el.getBoundingClientRect();
      // kleiner Puffer, damit nichts an der Unterkante klebt
      const bottomGap = 12;
      const next = Math.max(520, Math.floor(vvH - rect.top - bottomGap));
      setTodayCalendarHeight(next);
    };

    // next paint, dann messen (damit Top-Position final ist)
    rafId = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    try { window.visualViewport && window.visualViewport.addEventListener('resize', measure); } catch (_) {}
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
      try { window.visualViewport && window.visualViewport.removeEventListener('resize', measure); } catch (_) {}
    };
  }, [showTodayCalendar]);

  // Simple cookie helpers (same pattern as in GlobalCalendarView)
  const setCookie = useCallback((name, value, days = 365) => {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    } catch (_err) {
      // ignore cookie errors
    }
  }, []);

  const getCookie = useCallback((name) => {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (_err) {
      return null;
    }
  }, []);

  // Cookie key helper so preferences are per-user
  const getHiddenColumnsCookieKey = useCallback(() => {
    const email = getCurrentUserEmail();
    return email ? `dashboardHiddenColumns_${String(email).toLowerCase()}` : 'dashboardHiddenColumns';
  }, [getCurrentUserEmail]);

  // Load hidden columns from cookie when user is known
  useEffect(() => {
    if (!currentUser) return;
    const key = getHiddenColumnsCookieKey();
    const saved = getCookie(key);
    if (saved) {
      try {
        const layout = layoutAusCookie(JSON.parse(saved));
        if (layout) {
          setAusgeblendet(layout.ausgeblendet);
          setReihenfolge(layout.reihenfolge);
          setEingeblendet(layout.eingeblendet);
          setShowTodayCalendar(layout.showTodayCalendar);
        }
      } catch (_err) {
        // ignore parse errors
      }
    }
  }, [currentUser, getHiddenColumnsCookieKey, getCookie]);

  // #564: Stand vom Server holen (einmal je Seite, geteilt mit dem Abgleich in App.js)
  const einstellungen = useBenutzerEinstellungen();
  useEffect(() => {
    if (!currentUser) return;
    ladeEinstellungen().catch(() => null);
  }, [currentUser]);

  // #564: Server-Stand anwenden und ins Cookie spiegeln; ohne Server-Stand
  // einmal das bisherige Cookie übernehmen.
  const cookieUebernommen = useRef(false);
  useEffect(() => {
    if (!currentUser || !einstellungen) return;
    const key = getHiddenColumnsCookieKey();
    const layout = layoutAusEinstellungen(einstellungen.dashboard);
    if (layout) {
      setAusgeblendet(layout.ausgeblendet);
      setReihenfolge(layout.reihenfolge);
      setEingeblendet(layout.eingeblendet);
      setShowTodayCalendar(layout.showTodayCalendar);
      setCookie(key, JSON.stringify(cookieAusLayout(layout)));
      return;
    }
    if (cookieUebernommen.current) return;
    cookieUebernommen.current = true;
    try {
      const raw = getCookie(key);
      const teil = raw ? dashboardAenderung(undefined, JSON.parse(raw)) : {};
      if (Object.keys(teil).length > 0) aendereEinstellungen({ dashboard: teil }).catch(() => null);
    } catch (_) {
      // kaputtes Cookie: nichts zu übernehmen
    }
  }, [currentUser, einstellungen, getHiddenColumnsCookieKey, getCookie, setCookie]);

  const saveHiddenColumns = useCallback((nextState) => {
    const key = getHiddenColumnsCookieKey();
    let existing = {};
    try {
      const raw = getCookie(key);
      if (raw) existing = JSON.parse(raw);
    } catch (_) {}
    const updated = { ...existing, ...nextState };
    setCookie(key, JSON.stringify(updated));
    // #564: auf dem Server nur die geänderten Teile; scheitert das, bleibt das Cookie
    const bisher = einstellungenStand();
    aendereEinstellungen({ dashboard: dashboardAenderung(bisher && bisher.dashboard, nextState) }).catch(() => null);
  }, [getHiddenColumnsCookieKey, setCookie, getCookie]);

  // #569: einen beliebigen Bereich ein- oder ausblenden (Leiste „Ausgeblendete
  // Bereiche“). Mails und Teams laufen weiter über saveHiddenColumns.
  // #650: über bereiche.schalteBereich, damit Bereiche mit `standardAus`
  // über `eingeblendet` laufen; gewöhnliche ändern wie bisher nur `ausgeblendet`.
  const blendeBereich = useCallback((id, aus) => {
    const teil = schalteBereich({ ausgeblendet, eingeblendet }, id, !aus);
    const neuAus = teil.ausgeblendet || ausgeblendet;
    const neuEin = teil.eingeblendet || eingeblendet;
    setAusgeblendet(neuAus);
    setEingeblendet(neuEin);
    setCookie(getHiddenColumnsCookieKey(), JSON.stringify(cookieAusLayout({ ausgeblendet: neuAus, eingeblendet: neuEin, reihenfolge, showTodayCalendar })));
    aendereEinstellungen({ dashboard: teil }).catch(() => null);
  }, [ausgeblendet, eingeblendet, reihenfolge, showTodayCalendar, getHiddenColumnsCookieKey, setCookie]);

  const handleActivateTodayCalendar = useCallback(() => {
    setShowTodayCalendar(true);
    saveHiddenColumns({ showTodayCalendar: true });
  }, [saveHiddenColumns]);

  const handleDeactivateTodayCalendar = useCallback(() => {
    setShowTodayCalendar(false);
    saveHiddenColumns({ showTodayCalendar: false });
  }, [saveHiddenColumns]);

  return {
    hideEmails, setHideEmails, hideTeams, setHideTeams, showTodayCalendar,
    ausgeblendet, reihenfolge, eingeblendet, blendeBereich,
    todayCalendarWrapRef, todayCalendarHeight, ticketsSectionRef, ticketsHeaderRef, isTicketsNarrow,
    saveHiddenColumns, handleActivateTodayCalendar, handleDeactivateTodayCalendar
  };
}
